import React, { useEffect, useState } from "react";
import useAxiosClient from "../axios-client";

/**
 * The courier account balance, on the overview board.
 *
 * It earns a card because an account at zero rejects every booking, and the
 * outbox would surface that one parked consignment at a time — a slow, confusing
 * way to learn that nothing has shipped since Tuesday.
 *
 * Fetched on its own rather than folded into `/dashboard/overview`, for two
 * reasons: it is the only figure on the board that comes from someone else's
 * server, so a slow courier would otherwise hold up the whole dashboard on a
 * one-vCPU box; and it renders nothing at all until credentials are configured,
 * which is a state the overview payload should not have to carry.
 */
export default function CourierBalanceCard({ palette: c, cardBase, mono }) {
  const axiosClient = useAxiosClient();
  const [state, setState] = useState({ loading: true });

  useEffect(() => {
    let live = true;

    axiosClient
      .get("/courier/balance")
      .then(({ data }) => live && setState({ loading: false, ...data }))
      // Deliberately silent: a courier that will not answer is not a reason to
      // put a red toast over someone's dashboard. The card says so instead.
      .catch(() => live && setState({ loading: false, configured: true }));

    return () => {
      live = false;
    };
  }, [axiosClient]);

  // Nothing set up yet, or still asking — no card either way.
  if (state.loading || state.configured === false) return null;

  const unreachable = state.balance === null || state.balance === undefined;
  const low = !unreachable && state.balance <= 0;
  const tint = unreachable ? "#fbbf24" : low ? "#f87171" : "#34d399";

  return (
    <div
      style={{
        ...cardBase,
        borderRadius: 11,
        padding: "11px 13px",
        display: "flex",
        alignItems: "center",
        gap: 11,
        minWidth: 0,
      }}
    >
      <span
        style={{
          width: 30,
          height: 30,
          borderRadius: 9,
          background: "rgba(56,189,248,.12)",
          color: "#38bdf8",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 14,
          flexShrink: 0,
        }}
      >
        ৳
      </span>

      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 11.5, color: c.dim }}>Courier balance</div>
        <div
          style={{
            fontSize: 17,
            fontWeight: 700,
            color: tint,
            fontFamily: mono,
            lineHeight: 1.25,
          }}
        >
          {unreachable
            ? "—"
            : state.balance.toLocaleString("en-US", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
        </div>
        <div style={{ fontSize: 11.5, color: c.muted, marginTop: 1 }}>
          {unreachable
            ? "Courier did not answer"
            : low
              ? "Bookings will be rejected"
              : "Available for bookings"}
        </div>
      </div>
    </div>
  );
}
